import { Residue } from "@/types/residue/residue.types";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export const useResidueColumns = (
  t: (key: string) => string,
  onEdit: (residue: Residue) => void,
  onDelete: (residue: Residue) => void
): Column<Residue>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  const baseColumns: Column<Residue>[] = [
    {
      header: t("residues.table.name"),
      accessorKey: "name",
      sortable: true,
      className: "min-w-[180px]",
    },
    {
      header: t("residues.table.treatmentTypes"),
      accessorKey: "treatment_types",
      sortable: true,
      className: "min-w-[200px]",
      cell: (_: string, row: Residue) => (
        <div className="flex flex-wrap gap-1">
          {row.treatment_types?.map((treatment) => (
            <span
              key={treatment.id}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {t(`residues.treatments.${treatment.id}`)}
            </span>
          )) || "-"}
        </div>
      ),
    },
  ];

  const actionsColumn: Column<Residue> = {
    header: t("residues.table.actions"),
    accessorKey: "id",
    sortable: false,
    className: "w-[100px]",
    cell: (_: string, row: Residue) => (
      <div className="flex items-center gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(row)}
                className="h-8 w-8 hover:bg-gray-100"
              >
                <Pencil className="h-4 w-4 text-gray-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("residues.actions.edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(row)}
                className="h-8 w-8 hover:bg-gray-100 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4 text-gray-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("residues.actions.delete")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
};
